import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { TAX_TYPES } from '../../constants/taxTypes';
import { ACCOUNT_TYPES, inferAccountType } from '../../constants/accountTypes';
import { isIndividual, isForProfit, isNonProfit, isNPO, saveOrgInfo } from '../../constants/orgTypes';
import { formatCurrency, parseAmount, generateId, formatAmountInput, toHalfWidth } from '../../utils/formatting';
import { formatDate, clampDate } from '../../utils/date';
import { AccountSelect, MonthSelector, getDateRangeFromMonths, getMonthFromDate, getCurrentMonth } from '../common';
import QRCode from 'qrcode';

const API_URL = import.meta.env.VITE_API_URL;

// Helper function to save journal history
const saveJournalHistory = async (accountType, userEmail, fiscalYear, endMonth, historyEntry) => {
    const apiType = accountType === 'kojin' ? 'kojin' : 'houjin';
    // kojinの場合は必ず12月決算にする
    const actualEndMonth = apiType === 'kojin' ? 12 : endMonth;

    // BUG-3修正: userEmailの検証
    if (!userEmail) {
        console.error('saveJournalHistory: userEmail is required');
        throw new Error('ユーザー情報が取得できません。ログインし直してください。');
    }

    console.log('saveJournalHistory called:', {
        apiType,
        userEmail,
        fiscalYear,
        endMonth: actualEndMonth,
        originalEndMonth: endMonth,
        operationType: historyEntry.operationType,
        journalId: historyEntry.journalId
    });

    const response = await fetch(`${API_URL}?action=saveJournalHistory&type=${apiType}&user=${encodeURIComponent(userEmail)}&year=${fiscalYear}&endMonth=${actualEndMonth}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ history: historyEntry })
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('saveJournalHistory failed:', response.status, errorText);
        throw new Error(`履歴の保存に失敗しました (${response.status})`);
    }

    const data = await response.json();
    console.log('saveJournalHistory success:', data);
    return data;
};

// Helper function to get next entry number
const getNextEntryNumber = async (accountType, userEmail, fiscalYear, endMonth) => {
    const apiType = accountType === 'kojin' ? 'kojin' : 'houjin';
    const actualEndMonth = apiType === 'kojin' ? 12 : endMonth;

    if (!userEmail) {
        throw new Error('ユーザー情報が取得できません。');
    }

    const response = await fetch(`${API_URL}?action=getNextEntryNumber&type=${apiType}&user=${encodeURIComponent(userEmail)}&year=${fiscalYear}&endMonth=${actualEndMonth}`);

    if (!response.ok) {
        throw new Error('伝票番号の取得に失敗しました');
    }

    const data = await response.json();
    return data.entryNumber;
};

// 金額入力: onChange一本で全角→半角変換。IMEガード不要（数字フィールドはcomposition不使用）
const AmountInput = ({ value, onChange }) => {
    const inputRef = useRef(null);
    const [display, setDisplay] = useState(value === 0 ? '' : formatAmountInput(value));

    // フォーカス中でなければ外部からの値変更を反映（貸借自動補完など）
    useEffect(() => {
        if (inputRef.current && document.activeElement !== inputRef.current) {
            setDisplay(value === 0 ? '' : formatAmountInput(value));
        }
    }, [value]);

    const commit = (currentValue) => {
        const parsed = parseAmount(currentValue);
        onChange(parsed);
        setDisplay(parsed === 0 ? '' : formatAmountInput(parsed));
    };

    return (
        <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={display}
            onFocus={() => {
                // カンマを除いて生数字を表示
                const num = parseAmount(display);
                setDisplay(num === 0 ? '' : String(num));
            }}
            onChange={(e) => {
                // 全角→半角変換、数字以外除去。isComposingガード不要
                setDisplay(toHalfWidth(e.target.value).replace(/[^0-9]/g, ''));
            }}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    commit(inputRef.current.value);
                }
            }}
            onPaste={(e) => {
                e.preventDefault();
                const el = e.target;
                const pasted = (e.clipboardData || window.clipboardData).getData('text');
                const digits = toHalfWidth(pasted).replace(/[^0-9]/g, '');
                if (!digits) return;
                const s = el.selectionStart, end = el.selectionEnd;
                setDisplay(display.slice(0, s) + digits + display.slice(end));
            }}
            className="form-control"
            style={{ textAlign: 'right' }}
        />
    );
};

export const JournalEntry = ({ accounts, journals, setJournals, showAlert, currentFiscalYear, fiscalYears, focusJournalId, setFocusJournalId, deletedJournals, setDeletedJournals, fileSystem, saveData, accountType, orgType, user, orgInfo, setOrgInfo, creditCards, activeBookEmail }) => {
    const dataEmail = activeBookEmail || user?.email;
    const isKojin = accountType === 'kojin';
    const showCategory = !isIndividual(orgType) && !isForProfit(orgType);
    const showExpenseCategory = isNPO(orgType) || isNonProfit(orgType);

    // 現在の年度のendMonthを取得
    const currentEndMonth = useMemo(() => {
        const currentFY = fiscalYears?.find(fy => fy.year === currentFiscalYear);
        return currentFY?.endMonth || (isKojin ? 12 : 3);
    }, [fiscalYears, currentFiscalYear, isKojin]);

    // 年度の開始日と終了日を計算
    const getFiscalYearRange = useMemo(() => {
        // 決算月が12月なら年度=カレンダー年、それ以外なら翌年にまたがる
        const startMonth = currentEndMonth === 12 ? 1 : currentEndMonth + 1;
        const startYear = currentFiscalYear;
        const endYear = currentEndMonth === 12 ? currentFiscalYear : currentFiscalYear + 1;

        // 終了日は決算月の最終日
        const endDay = new Date(endYear, currentEndMonth, 0).getDate();

        return {
            start: `${startYear}-${String(startMonth).padStart(2, '0')}-01`,
            end: `${endYear}-${String(currentEndMonth).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`
        };
    }, [currentFiscalYear, currentEndMonth]);

    // 年度内の適切なデフォルト日付を取得
    const getDefaultDate = () => {
        // 現在の日付を YYYY-MM-DD 形式に
        const todayStr = new Date().toISOString().split('T')[0];

        // 今日が年度内なら今日、年度前なら開始日、年度後なら終了日
        if (todayStr < getFiscalYearRange.start) {
            return getFiscalYearRange.start;
        } else if (todayStr > getFiscalYearRange.end) {
            return getFiscalYearRange.end;
        } else {
            return todayStr;
        }
    };

    const [halfWidthInput, setHalfWidthInput] = useState(false);
    const [date, setDate] = useState(getDefaultDate());
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('非収益');
    const [expenseCategory, setExpenseCategory] = useState('事業費');
    const [lines, setLines] = useState([
        { id: generateId(), type: 'debit', accountId: '', amount: 0, taxType: TAX_TYPES.NON_TAXABLE, taxRate: 0, invoiceNumber: '', vendorName: '', vendorMemo: '' },
        { id: generateId(), type: 'credit', accountId: '', amount: 0, taxType: TAX_TYPES.NON_TAXABLE, taxRate: 0, invoiceNumber: '', vendorName: '', vendorMemo: '' }
    ]);

    // スマホ連携用状態
    const [showQRModal, setShowQRModal] = useState(false);
    const [qrDataUrl, setQrDataUrl] = useState(null);
    const [isGeneratingQR, setIsGeneratingQR] = useState(false);

    // QRコード生成
    const generateQRCode = async () => {
        if (!user?.email) {
            showAlert('ログインが必要です', 'error');
            return;
        }

        setIsGeneratingQR(true);
        setShowQRModal(true);
        try {
            const res = await fetch(`${API_URL}?action=generateToken&user=${encodeURIComponent(user.email)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            const data = await res.json();

            if (data.success && data.token) {
                const qrUrl = `toiro-accounting://connect?token=${data.token}`;
                const dataUrl = await QRCode.toDataURL(qrUrl, {
                    width: 256,
                    margin: 2,
                    color: { dark: '#1976d2', light: '#ffffff' }
                });
                setQrDataUrl(dataUrl);
            } else {
                showAlert('QRコード生成に失敗しました', 'error');
                setShowQRModal(false);
            }
        } catch (error) {
            console.error('QR generation error:', error);
            showAlert('QRコード生成に失敗しました', 'error');
            setShowQRModal(false);
        } finally {
            setIsGeneratingQR(false);
        }
    };

    const [vendors, setVendors] = useState(() => {
        return orgInfo?.vendors || [];
    });
    const [lookingUpInvoice, setLookingUpInvoice] = useState(null);

    const lookupInvoiceNumber = async (lineId, invoiceNumber) => {
        if (!invoiceNumber || invoiceNumber.length !== 13) return;
        setLookingUpInvoice(lineId);
        try {
            const response = await fetch(`https://web-api.invoice-kohyo.nta.go.jp/1/num?id=T${invoiceNumber}`);
            if (response.ok) {
                const data = await response.json();
                if (data.announcement && data.announcement.length > 0) {
                    const vendor = data.announcement[0];
                    const vendorName = vendor.name || '';
                    setLines(prev => prev.map(l =>
                        l.id === lineId ? { ...l, vendorName } : l
                    ));
                }
            }
        } catch (err) {
            console.error('Invoice lookup failed:', err);
        } finally {
            setLookingUpInvoice(null);
        }
    };

    const saveVendor = (invoiceNumber, vendorName, vendorMemo) => {
        if (!invoiceNumber || !vendorName) return;
        const existing = vendors.find(v => v.invoiceNumber === invoiceNumber);
        if (existing) {
            const updated = vendors.map(v =>
                v.invoiceNumber === invoiceNumber ? { ...v, vendorName, vendorMemo } : v
            );
            setVendors(updated);
            const updatedOrgInfo = { ...orgInfo, vendors: updated };
            setOrgInfo(updatedOrgInfo);
            saveOrgInfo(updatedOrgInfo, dataEmail);
        } else {
            const newVendors = [...vendors, { invoiceNumber, vendorName, vendorMemo }];
            setVendors(newVendors);
            const updatedOrgInfo = { ...orgInfo, vendors: newVendors };
            setOrgInfo(updatedOrgInfo);
            saveOrgInfo(updatedOrgInfo, dataEmail);
        }
    };

    const selectVendor = (lineId, vendor) => {
        if (vendor) {
            setLines(prev => prev.map(l =>
                l.id === lineId ? {
                    ...l,
                    invoiceNumber: vendor.invoiceNumber,
                    vendorName: vendor.vendorName,
                    vendorMemo: vendor.vendorMemo || ''
                } : l
            ));
        }
    };

    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAuditTrail, setShowAuditTrail] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const currentMonth = getCurrentMonth(currentFiscalYear, isKojin);
    const [selectedMonths, setSelectedMonths] = useState([currentMonth || 0]);

    const focusRowRef = useRef(null);
    useEffect(() => {
        if (focusJournalId) {
            const targetJournal = journals.find(j => j.id === focusJournalId);
            if (targetJournal) {
                const targetMonth = getMonthFromDate(targetJournal.date, currentFiscalYear, isKojin);
                if (targetMonth > 0) {
                    setSelectedMonths([targetMonth]);
                } else {
                    setSelectedMonths([0]);
                }
            }
        }
    }, [focusJournalId, journals, currentFiscalYear, isKojin]);

    useEffect(() => {
        if (focusJournalId && focusRowRef.current) {
            focusRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => setFocusJournalId && setFocusJournalId(null), 2000);
        }
    }, [focusJournalId, selectedMonths, setFocusJournalId]);

    const { dateFrom, dateTo } = getDateRangeFromMonths(selectedMonths, currentFiscalYear, isKojin);

    const sortedAccounts = useMemo(() => {
        return [...accounts].sort((a, b) => {
            const typeOrder = { asset: 1, liability: 2, equity: 3, revenue: 4, expense: 5 };
            if (typeOrder[a.type] !== typeOrder[b.type]) {
                return typeOrder[a.type] - typeOrder[b.type];
            }
            return a.code.localeCompare(b.code);
        });
    }, [accounts]);

    const addLine = (type) => {
        setLines([...lines, { id: generateId(), type, accountId: '', amount: 0, taxType: TAX_TYPES.NON_TAXABLE, taxRate: 0, invoiceNumber: '', vendorName: '', vendorMemo: '' }]);
    };

    const updateLine = (id, field, value) => {
        setLines(prevLines => {
            const newLines = prevLines.map(line =>
                line.id === id ? { ...line, [field]: field === 'amount' ? parseAmount(value) : value } : line
            );

            if (field === 'accountId' && value) {
                const selectedAccount = accounts.find(a => a.id === value);
                const currentLine = newLines.find(l => l.id === id);
                const oppositeType = currentLine?.type === 'debit' ? 'credit' : 'debit';
                const emptyOpposite = newLines.find(l => l.type === oppositeType && !l.accountId);
                if (emptyOpposite) {
                    const counterpartAccount = (selectedAccount?.counterpart
                        ? accounts.find(a => a.name === selectedAccount.counterpart)
                        : null) || sortedAccounts[0];
                    if (counterpartAccount) {
                        return newLines.map(line =>
                            line.id === emptyOpposite.id ? { ...line, accountId: counterpartAccount.id } : line
                        );
                    }
                }
            }

            if (field === 'amount') {
                const debitLines = newLines.filter(l => l.type === 'debit');
                const creditLines = newLines.filter(l => l.type === 'credit');
                if (debitLines.length === 1 && creditLines.length === 1) {
                    const currentLine = newLines.find(l => l.id === id);
                    const oppositeType = currentLine?.type === 'debit' ? 'credit' : 'debit';
                    const oppositeLine = newLines.find(l => l.type === oppositeType);
                    if (oppositeLine) {
                        const parsedAmount = parseAmount(value);
                        return newLines.map(line =>
                            line.id === oppositeLine.id ? { ...line, amount: parsedAmount } : line
                        );
                    }
                }
            }

            return newLines;
        });
    };

    const removeLine = (id) => {
        if (lines.length > 2) {
            setLines(lines.filter(line => line.id !== id));
        }
    };

    const debitTotal = lines.filter(l => l.type === 'debit').reduce((sum, l) => sum + l.amount, 0);
    const creditTotal = lines.filter(l => l.type === 'credit').reduce((sum, l) => sum + l.amount, 0);
    const isBalanced = debitTotal === creditTotal && debitTotal > 0;

    const handleSubmit = async () => {
        if (!date) {
            showAlert('日付を入力してください', 'error');
            return;
        }
        if (!description) {
            showAlert('摘要を入力してください', 'error');
            return;
        }
        if (!isBalanced) {
            showAlert('借方と貸方の合計が一致していません', 'error');
            return;
        }
        if (lines.some(l => !l.accountId)) {
            showAlert('勘定科目を選択してください', 'error');
            return;
        }

        setIsSubmitting(true);

        try {
            const journal = {
                id: editingId || generateId(),
                date,
                description,
                category,
                expenseCategory: showExpenseCategory ? expenseCategory : '',
                lines: lines.map(l => ({
                    accountId: l.accountId,
                    type: l.type,
                    amount: l.amount,
                    taxType: l.taxType || TAX_TYPES.NON_TAXABLE,
                    taxRate: l.taxRate || 0,
                    invoiceNumber: l.invoiceNumber || '',
                    vendorName: l.vendorName || '',
                    vendorMemo: l.vendorMemo || '',
                    ...(l.creditCardId ? { creditCardId: l.creditCardId } : {})
                })),
                createdAt: editingId ? journals.find(j => j.id === editingId)?.createdAt : new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            if (editingId) {
                // 編集時：既存の伝票番号を引き継ぎ、履歴を記録
                const existingJournal = journals.find(j => j.id === editingId);
                if (existingJournal?.entryNumber) {
                    journal.entryNumber = existingJournal.entryNumber;
                }

                const historyEntry = {
                    historyId: generateId(),
                    journalId: journal.id,
                    entryNumber: journal.entryNumber || null,
                    operationType: 'UPDATE',
                    operationTimestamp: new Date().toISOString(),
                    operatorEmail: user?.email || null,
                    operatorName: orgInfo?.name || user?.name || null,
                    beforeSnapshot: existingJournal,
                    afterSnapshot: journal
                };

                await saveJournalHistory(accountType, dataEmail, currentFiscalYear, currentEndMonth, historyEntry);
            } else {
                // 新規作成時：伝票番号をAPIから取得（履歴は記録しない）
                const entryNumber = await getNextEntryNumber(accountType, dataEmail, currentFiscalYear, currentEndMonth);
                journal.entryNumber = entryNumber;
            }

            lines.forEach(l => {
                if (l.taxRate >= 8 && l.invoiceNumber && l.vendorName && !vendors.find(v => v.invoiceNumber === l.invoiceNumber)) {
                    saveVendor(l.invoiceNumber, l.vendorName, l.vendorMemo);
                }
            });

            if (editingId) {
                setJournals(journals.map(j => j.id === editingId ? journal : j));
                showAlert('仕訳を更新しました');
                setEditingId(null);
                setDate(getDefaultDate()); // 編集完了後はデフォルトに戻す
            } else {
                setJournals([...journals, journal]);
                showAlert('仕訳を登録しました');
                // 新規登録後は日付をそのまま維持（連続入力時の打ち直し防止）
            }
            setDescription('');
            setCategory('非収益');
            setExpenseCategory('事業費');
            setLines([
                { id: generateId(), type: 'debit', accountId: '', amount: 0, taxType: TAX_TYPES.NON_TAXABLE, taxRate: 0, invoiceNumber: '', vendorName: '', vendorMemo: '' },
                { id: generateId(), type: 'credit', accountId: '', amount: 0, taxType: TAX_TYPES.NON_TAXABLE, taxRate: 0, invoiceNumber: '', vendorName: '', vendorMemo: '' }
            ]);
        } catch (err) {
            console.error('Journal save error:', err);
            showAlert('保存に失敗しました: ' + err.message, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (journal) => {
        setEditingId(journal.id);
        setDate(journal.date);
        setDescription(journal.description);
        setCategory(journal.category || '非収益');
        setExpenseCategory(journal.expenseCategory || '事業費');
        setLines(journal.lines.map(l => ({
            id: generateId(),
            type: l.type,
            accountId: l.accountId,
            amount: l.amount,
            taxType: l.taxType || TAX_TYPES.NON_TAXABLE,
            taxRate: l.taxRate || 0,
            invoiceNumber: l.invoiceNumber || '',
            vendorName: l.vendorName || '',
            vendorMemo: l.vendorMemo || '',
            creditCardId: l.creditCardId || ''
        })));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (confirm('この仕訳を削除してよろしいですか？')) {
            setIsSubmitting(true);
            try {
                const journalToDelete = journals.find(j => j.id === id);
                if (journalToDelete) {
                    const deleteTimestamp = new Date().toISOString();

                    const historyEntry = {
                        historyId: generateId(),
                        journalId: id,
                        entryNumber: journalToDelete.entryNumber || null,
                        operationType: 'DELETE',
                        operationTimestamp: deleteTimestamp,
                        operatorEmail: user?.email || null,
                        operatorName: orgInfo?.name || user?.name || null,
                        beforeSnapshot: journalToDelete,
                        afterSnapshot: null
                    };
                    await saveJournalHistory(accountType, dataEmail, currentFiscalYear, currentEndMonth, historyEntry);

                    const deletedRecord = {
                        ...journalToDelete,
                        deletedAt: deleteTimestamp,
                        deletedBy: user?.email || null,
                        deletedByName: orgInfo?.name || user?.name || null
                    };
                    setDeletedJournals(prev => [...prev, deletedRecord]);
                }
                setJournals(prev => prev.filter(j => j.id !== id));
                showAlert('仕訳を削除しました');
            } catch (err) {
                console.error('Journal delete error:', err);
                showAlert('削除に失敗しました: ' + err.message, 'error');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleCancel = () => {
        setEditingId(null);
        setDate(getDefaultDate());
        setDescription('');
        setCategory('非収益');
        setExpenseCategory('事業費');
        setLines([
            { id: generateId(), type: 'debit', accountId: '', amount: 0, taxType: TAX_TYPES.NON_TAXABLE, taxRate: 0, invoiceNumber: '', vendorName: '', vendorMemo: '' },
            { id: generateId(), type: 'credit', accountId: '', amount: 0, taxType: TAX_TYPES.NON_TAXABLE, taxRate: 0, invoiceNumber: '', vendorName: '', vendorMemo: '' }
        ]);
    };


    // Filter journals by date range and search term
    const filteredJournals = useMemo(() => {
        return journals.filter(journal => {
            // Date range filter
            if (dateFrom && journal.date < dateFrom) return false;
            if (dateTo && journal.date > dateTo) return false;

            // Search filter
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                const matchDescription = journal.description?.toLowerCase().includes(term);
                const matchDate = journal.date?.includes(term);
                const matchAmount = journal.lines?.some(l => String(l.amount).includes(term));
                const matchAccount = journal.lines?.some(l => {
                    const acc = accounts.find(a => a.id === l.accountId);
                    return acc?.name?.toLowerCase().includes(term);
                });
                return matchDescription || matchDate || matchAmount || matchAccount;
            }
            return true;
        }).sort((a, b) => b.date.localeCompare(a.date));
    }, [journals, dateFrom, dateTo, searchTerm, accounts]);

    return (
        <div className="page-container">
            {/* Loading overlay */}
            {isSubmitting && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div className="spinner" style={{ marginBottom: '20px' }}></div>
                    <p style={{ color: '#fff', fontSize: '16px', fontWeight: '500' }}>処理中...</p>
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0 }}>仕訳入力</h2>
                <button
                    className="btn btn-primary"
                    onClick={generateQRCode}
                    disabled={isGeneratingQR}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                    <span style={{ fontSize: '16px' }}>📱</span>
                    スマホで仕訳
                </button>
            </div>

            {/* QRコードモーダル */}
            {showQRModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }} onClick={() => setShowQRModal(false)}>
                    <div style={{
                        background: '#fff',
                        borderRadius: '12px',
                        padding: '32px',
                        maxWidth: '400px',
                        textAlign: 'center'
                    }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ marginBottom: '16px' }}>スマホで仕訳</h3>
                        {isGeneratingQR ? (
                            <div style={{ padding: '40px' }}>
                                <div className="spinner"></div>
                                <p style={{ marginTop: '12px', color: '#666' }}>QRコード生成中...</p>
                            </div>
                        ) : qrDataUrl ? (
                            <>
                                <img src={qrDataUrl} alt="QRコード" style={{ width: '256px', height: '256px' }} />
                                <p style={{ marginTop: '16px', color: '#666', fontSize: '13px' }}>
                                    スマホアプリでこのQRコードを読み取ってください
                                </p>
                            </>
                        ) : null}
                        <button
                            className="btn"
                            onClick={() => setShowQRModal(false)}
                            style={{ marginTop: '20px' }}
                        >
                            閉じる
                        </button>
                    </div>
                </div>
            )}

            {/* Entry Form */}
            <div className="card" style={{ marginBottom: '24px' }}>

                <div className="form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                        <label>日付</label>
                        <input
                            type="date"
                            value={date}
                            onChange={e => setDate(clampDate(e.target.value))}
                            min={getFiscalYearRange.start}
                            max={getFiscalYearRange.end}
                            className="form-control"
                        />
                    </div>
                    <div className="form-group" style={{ flex: 3 }}>
                        <label>摘要</label>
                        <input
                            type="text"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            className="form-control"
                            placeholder="取引の内容を入力"
                        />
                    </div>
                    {showCategory && (
                        <div className="form-group" style={{ flex: 1 }}>
                            <label>区分</label>
                            <select
                                value={category}
                                onChange={e => setCategory(e.target.value)}
                                className="form-control"
                            >
                                <option value="非収益">非収益</option>
                                <option value="収益">収益</option>
                                <option value="共通">共通</option>
                            </select>
                        </div>
                    )}
                </div>

                {/* Journal Lines */}
                <table className="data-table" style={{ marginBottom: '16px' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '80px' }}>借/貸</th>
                            <th>
                                勘定科目
                                <label style={{ marginLeft: '8px', fontSize: '12px', fontWeight: 'normal', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                    <input
                                        type="checkbox"
                                        checked={halfWidthInput}
                                        onChange={e => setHalfWidthInput(e.target.checked)}
                                        style={{ marginRight: '3px' }}
                                    />
                                    半角入力
                                </label>
                            </th>
                            <th style={{ width: '150px' }}>金額</th>
                            <th style={{ width: '100px' }}>税区分</th>
                            <th style={{ width: '50px' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map(line => (
                            <tr key={line.id}>
                                <td>
                                    <select
                                        value={line.type}
                                        onChange={e => updateLine(line.id, 'type', e.target.value)}
                                        className="form-control"
                                    >
                                        <option value="debit">借方</option>
                                        <option value="credit">貸方</option>
                                    </select>
                                </td>
                                <td>
                                    <AccountSelect
                                        accounts={sortedAccounts}
                                        value={line.accountId}
                                        onChange={id => updateLine(line.id, 'accountId', id)}
                                        halfWidth={halfWidthInput}
                                    />
                                    {line.type === 'credit' && creditCards?.length > 0 && (() => {
                                        const matchingCards = creditCards.filter(c => c.unpaidAccountId === line.accountId);
                                        if (matchingCards.length === 0) return null;
                                        return (
                                            <select
                                                className="form-control"
                                                style={{ marginTop: '4px', fontSize: '12px' }}
                                                value={line.creditCardId || ''}
                                                onChange={e => updateLine(line.id, 'creditCardId', e.target.value || '')}
                                            >
                                                <option value="">カードなし</option>
                                                {matchingCards.map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                        );
                                    })()}
                                </td>
                                <td>
                                    <AmountInput
                                        value={line.amount}
                                        onChange={v => updateLine(line.id, 'amount', v)}
                                    />
                                </td>
                                <td>
                                    <select
                                        value={`${line.taxType}-${line.taxRate}`}
                                        onChange={e => {
                                            const lastDash = e.target.value.lastIndexOf('-');
                                            const taxType = e.target.value.substring(0, lastDash);
                                            const taxRate = parseInt(e.target.value.substring(lastDash + 1)) || 0;
                                            updateLine(line.id, 'taxType', taxType);
                                            updateLine(line.id, 'taxRate', taxRate);
                                        }}
                                        className="form-control"
                                    >
                                        <option value={`${TAX_TYPES.NON_TAXABLE}-0`}>対象外</option>
                                        <option value={`${TAX_TYPES.TAXABLE}-10`}>課税10%</option>
                                        <option value={`${TAX_TYPES.TAXABLE}-8`}>課税8%</option>
                                        <option value={`${TAX_TYPES.TAX_FREE}-0`}>非課税</option>
                                    </select>
                                </td>
                                <td>
                                    {lines.length > 2 && (
                                        <button
                                            className="btn btn-danger btn-small"
                                            onClick={() => removeLine(line.id)}
                                        >
                                            -
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    <button className="btn btn-secondary" onClick={() => addLine('debit')}>+ 借方行</button>
                    <button className="btn btn-secondary" onClick={() => addLine('credit')}>+ 貸方行</button>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <span style={{ marginRight: '20px' }}>借方合計: {formatCurrency(debitTotal)}</span>
                        <span style={{ marginRight: '20px' }}>貸方合計: {formatCurrency(creditTotal)}</span>
                        {!isBalanced && debitTotal + creditTotal > 0 && (
                            <span style={{ color: '#e74c3c' }}>差額: {formatCurrency(Math.abs(debitTotal - creditTotal))}</span>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {editingId && (
                            <button className="btn btn-secondary" onClick={handleCancel}>
                                キャンセル
                            </button>
                        )}
                        <button
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={!isBalanced || isSubmitting}
                        >
                            {isSubmitting ? '保存中...' : editingId ? '更新' : '登録'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Journal List */}
            <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3>仕訳一覧</h3>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                            type="text"
                            placeholder="検索..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="form-control"
                            style={{ width: '200px' }}
                        />
                        <MonthSelector
                            selectedMonths={selectedMonths}
                            onChange={setSelectedMonths}
                            fiscalYear={currentFiscalYear}
                            isKojin={isKojin}
                        />
                        <button
                            className={`btn ${showAuditTrail ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setShowAuditTrail(!showAuditTrail)}
                        >
                            監査証跡
                        </button>
                    </div>
                </div>

                {filteredJournals.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
                        仕訳がありません
                    </p>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th style={{ width: '90px', padding: '6px 8px' }}>{showAuditTrail ? '入力日' : '日付'}</th>
                                <th style={{ padding: '6px 8px' }}>摘要</th>
                                <th style={{ padding: '6px 8px' }}>借方</th>
                                <th style={{ padding: '6px 8px' }}>貸方</th>
                                <th style={{ textAlign: 'right', width: '100px', padding: '6px 8px' }}>金額</th>
                                <th style={{ width: '110px', padding: '6px 8px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredJournals.map(journal => {
                                const debitLine = journal.lines.find(l => l.type === 'debit');
                                const creditLine = journal.lines.find(l => l.type === 'credit');
                                const debitAccount = accounts.find(a => a.id === debitLine?.accountId);
                                const creditAccount = accounts.find(a => a.id === creditLine?.accountId);
                                const isFocused = journal.id === focusJournalId;

                                return (
                                    <tr
                                        key={journal.id}
                                        ref={isFocused ? focusRowRef : null}
                                        style={{ ...(isFocused ? { backgroundColor: '#fff3cd' } : {}), lineHeight: '1.2' }}
                                    >
                                        <td style={{ padding: '4px 8px' }}>
                                            {showAuditTrail ? (
                                                // インポート判定: isImportedフラグ、または createdAtが無効な日付の場合
                                                (() => {
                                                    const isImported = journal.isImported ||
                                                        !journal.createdAt ||
                                                        journal.createdAt === '他ソフトからインポート' ||
                                                        isNaN(new Date(journal.createdAt).getTime());
                                                    if (isImported) {
                                                        const importDate = journal.importedAt
                                                            ? new Date(journal.importedAt).toLocaleDateString('ja-JP', {year: 'numeric', month: '2-digit', day: '2-digit'}).replace(/\//g, '/')
                                                            : null;
                                                        return (
                                                            <span style={{color: '#555', fontSize: '10px', display: 'flex', alignItems: 'center', gap: '2px', whiteSpace: 'nowrap'}}>
                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink: 0}}>
                                                                    <path d="M12 2L12 14M12 14L7 9M12 14L17 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                                                                    <path d="M4 17V19C4 20.1 4.9 21 6 21H18C19.1 21 20 20.1 20 19V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
                                                                </svg>
                                                                {importDate || '(日時不明)'}
                                                            </span>
                                                        );
                                                    }
                                                    return formatDate(journal.createdAt);
                                                })()
                                            ) : (
                                                formatDate(journal.date)
                                            )}
                                        </td>
                                        <td style={{ padding: '4px 8px' }}>{journal.description}</td>
                                        <td style={{ padding: '4px 8px' }}>
                                            {debitAccount?.name}
                                            {journal.lines.filter(l => l.type === 'debit').length > 1 && ' 他'}
                                        </td>
                                        <td style={{ padding: '4px 8px' }}>
                                            {creditAccount?.name}
                                            {journal.lines.filter(l => l.type === 'credit').length > 1 && ' 他'}
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '4px 8px' }}>
                                            {formatCurrency(debitLine?.amount || 0)}
                                        </td>
                                        <td style={{ padding: '4px 8px' }}>
                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                <button
                                                    className="btn btn-small"
                                                    onClick={() => handleEdit(journal)}
                                                    style={{ padding: '2px 8px', fontSize: '12px' }}
                                                >
                                                    編集
                                                </button>
                                                <button
                                                    className="btn btn-small btn-danger"
                                                    onClick={() => handleDelete(journal.id)}
                                                    style={{ padding: '2px 8px', fontSize: '12px' }}
                                                >
                                                    削除
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

        </div>
    );
};

export default JournalEntry;
